'use client'

import React, { useState, useEffect, useRef } from 'react'
import { cn } from '../../../utils/styles'
import {
  generateThumbnailCached,
  cleanupThumbnail,
  type ThumbnailResult,
  type ThumbnailOptions
} from '../utils/thumbnailUtils'
import { isImageFile, getFileIcon } from '../utils/fileUtils'
import type { FileThumbnailProps } from '../types'

export function FileThumbnail({
  file,
  size = 64,
  className,
  fallbackIcon,
  lazy = true,
  onLoad,
  onError
}: FileThumbnailProps) {
  const [thumbnail, setThumbnail] = useState<ThumbnailResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [isVisible, setIsVisible] = useState(!lazy)
  const containerRef = useRef<HTMLDivElement>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!lazy || isVisible) return

    const container = containerRef.current
    if (!container) return

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (entry.isIntersecting) {
          setIsVisible(true)
          observerRef.current?.disconnect()
        }
      },
      { threshold: 0.1 }
    )

    observerRef.current.observe(container)

    return () => {
      observerRef.current?.disconnect()
    }
  }, [lazy, isVisible])

  // Generate thumbnail when visible
  useEffect(() => {
    if (!isVisible || thumbnail || isLoading) return

    const generateThumbnail = async () => {
      setIsLoading(true)
      setHasError(false)

      try {
        const options: ThumbnailOptions = {
          size,
          quality: 0.8,
          format: 'image/jpeg',
          crop: true
        }

        const result = await generateThumbnailCached(file, options)
        setThumbnail(result)
        onLoad?.()
      } catch (error) {
        console.warn('Failed to generate thumbnail:', error)
        setHasError(true)
        onError?.()
      } finally {
        setIsLoading(false)
      }
    }

    generateThumbnail()
  }, [isVisible, file, size, thumbnail, isLoading, onLoad, onError])

  // Cleanup thumbnail on unmount
  useEffect(() => {
    return () => {
      if (thumbnail) {
        cleanupThumbnail(thumbnail)
      }
    }
  }, [thumbnail])

  const renderFallback = () => {
    if (fallbackIcon) {
      return fallbackIcon
    }

    const icon = getFileIcon(file)
    return (
      <div className="flex items-center justify-center text-2xl">
        {icon}
      </div>
    )
  }

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-blue-600" />
        </div>
      )
    }

    if (hasError || !thumbnail) {
      return renderFallback()
    }

    return (
      <img
        src={thumbnail.dataUrl}
        alt={`Thumbnail of ${file.name}`}
        className="w-full h-full object-cover"
        onError={() => {
          setHasError(true)
          onError?.()
        }}
      />
    )
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        'flex items-center justify-center overflow-hidden bg-gray-100 border border-gray-200 rounded',
        'flex-shrink-0',
        className
      )}
      style={{ width: size, height: size }}
      title={file.name}
    >
      {renderContent()}
    </div>
  )
}

// Enhanced version with more customization options
export interface FileThumbnailEnhancedProps extends FileThumbnailProps {
  variant?: 'square' | 'rounded' | 'circle'
  border?: boolean
  shadow?: boolean
  background?: string
  overlay?: React.ReactNode
  onClick?: () => void
}

export function FileThumbnailEnhanced({
  variant = 'rounded',
  border = true,
  shadow = false,
  background = 'bg-gray-100',
  overlay,
  onClick,
  className,
  ...props
}: FileThumbnailEnhancedProps) {
  const variantClasses = {
    square: 'rounded-none',
    rounded: 'rounded',
    circle: 'rounded-full'
  }

  return (
    <div
      className={cn(
        'relative overflow-hidden',
        background,
        border && 'border border-gray-200',
        shadow && 'shadow-sm',
        variantClasses[variant],
        onClick && 'cursor-pointer hover:opacity-80 transition-opacity',
        className
      )}
      onClick={onClick}
      style={{ width: props.size, height: props.size }}
    >
      <FileThumbnail {...props} className="w-full h-full" />
      {overlay && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
          {overlay}
        </div>
      )}
    </div>
  )
}

// Grid layout for multiple thumbnails
export interface FileThumbnailGridProps {
  files: File[]
  thumbnailSize?: number
  maxVisible?: number
  onThumbnailClick?: (file: File, index: number) => void
  className?: string
}

export function FileThumbnailGrid({
  files,
  thumbnailSize = 64,
  maxVisible = 4,
  onThumbnailClick,
  className
}: FileThumbnailGridProps) {
  const visibleFiles = files.slice(0, maxVisible)
  const remainingCount = files.length - maxVisible

  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {visibleFiles.map((file, index) => (
        <FileThumbnailEnhanced
          key={`${file.name}-${file.size}-${index}`}
          file={file}
          size={thumbnailSize}
          onClick={() => onThumbnailClick?.(file, index)}
          variant="rounded"
          border
          shadow
        />
      ))}

      {remainingCount > 0 && (
        <div
          className={cn(
            'flex items-center justify-center bg-gray-200 border border-gray-300 rounded text-sm font-medium text-gray-600',
            onThumbnailClick && 'cursor-pointer hover:bg-gray-300'
          )}
          style={{ width: thumbnailSize, height: thumbnailSize }}
          onClick={() => onThumbnailClick?.(files[maxVisible], maxVisible)}
        >
          +{remainingCount}
        </div>
      )}
    </div>
  )
}